import React, { useState } from 'react';
import { Phone, User, ArrowRight, Check, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../lib/api';
import { businessTypes } from '../mock';
import { toast } from '../hooks/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const WhatsAppOptIn = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('other');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast({ title: 'Missing details', description: 'Add your name and UK mobile number.' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/whatsapp/subscribe', { name, phone, businessName, businessType });
      // Track for the founder dashboard
      try { await api.post('/track/event', { type: 'whatsapp_subscribe', label: 'landing_form' }); } catch {}
      setSubmitted(true);
      toast({ title: 'You’re in!', description: 'Opening WhatsApp so you can say hi…' });
      // Pop a wa.me deep link in a new tab so the user can chat with us
      // directly. The Mongo lead is already captured server-side; this gives
      // them an instant follow-up channel and lets us reply manually.
      const greeting = `Hi! I'm ${name.trim()}${businessName ? ` from ${businessName}` : ''} and I just signed up for MyBusinessAdPro WhatsApp updates.`;
      const url = `https://wa.me/447448574616?text=${encodeURIComponent(greeting)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({ title: 'Could not subscribe', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-8 lg:p-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wider text-emerald-700">WhatsApp updates</span>
            </div>
            <h2 className="mt-4 text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Get tips, launch news &amp; early discounts — on WhatsApp.
            </h2>
            <p className="mt-3 text-slate-600">
              We send 1–2 short messages a month. Marketing tips for UK small businesses, plus first dibs on new features. Unsubscribe with one tap.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-600" strokeWidth={3} />
              </div>
              <h3 className="mt-4 text-xl font-bold text-slate-900">You’re on the list!</h3>
              <p className="mt-1 text-slate-600 text-sm">
                We’ve opened WhatsApp for you — say hi and we’ll reply with your welcome offer.
              </p>
              <a
                href={`https://wa.me/447448574616?text=${encodeURIComponent(`Hi! I'm ${name.trim()}${businessName ? ` from ${businessName}` : ''} and I just signed up for MyBusinessAdPro WhatsApp updates.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-5 h-11 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold rounded-xl"
                data-testid="whatsapp-success-open-chat"
              >
                <MessageCircle className="w-4 h-4" /> Open WhatsApp chat
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="pl-10 h-11" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="UK mobile e.g. 07712 345 678" className="pl-10 h-11" />
              </div>
              <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Business name (optional)" className="h-11" />
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Business type" /></SelectTrigger>
                <SelectContent>
                  {businessTypes.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold rounded-xl">
                {loading ? 'Subscribing…' : (<>Join WhatsApp updates <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
              <p className="text-[11px] text-slate-400 text-center">We never share your number. Unsubscribe anytime.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhatsAppOptIn;
